import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { OpsLayout } from '@/components/ops/OpsLayout';
import { OnboardingTierSelector } from '@/components/onboarding-hub/OnboardingTierSelector';
import { DataIngestionPortal } from '@/components/onboarding-hub/DataIngestionPortal';
import { AICustomizationEngine } from '@/components/onboarding-hub/AICustomizationEngine';
import { SurgeonWelcomeScreen } from '@/components/onboarding-hub/SurgeonWelcomeScreen';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

export type OnboardingTier = 'single_surgeon' | 'small_practice' | 'enterprise';
export type HubView = 'tier_select' | 'data_ingestion' | 'ai_processing' | 'surgeon_welcome';

export default function OnboardingHub() {
  const navigate = useNavigate();
  const [selectedTier, setSelectedTier] = useState<OnboardingTier | null>(null);
  const [currentView, setCurrentView] = useState<HubView>('tier_select');

  const handleTierSelect = (tier: OnboardingTier) => {
    setSelectedTier(tier);
    setCurrentView('data_ingestion');
  };

  const handleDataComplete = () => {
    setCurrentView('ai_processing');
  };

  const handleProcessingComplete = () => {
    setCurrentView('surgeon_welcome');
  };

  const handleBack = () => {
    switch (currentView) {
      case 'data_ingestion':
        setCurrentView('tier_select');
        break;
      case 'ai_processing':
        setCurrentView('data_ingestion');
        break;
      case 'surgeon_welcome':
        setCurrentView('ai_processing');
        break;
      default:
        break;
    }
  };

  return (
    <OpsLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          {currentView !== 'tier_select' && (
            <Button variant="ghost" size="icon" onClick={handleBack}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-foreground tracking-tight">Onboarding Hub</h1>
            <p className="text-sm text-muted-foreground">
              {currentView === 'tier_select' && 'Select the deployment tier to begin setup'}
              {currentView === 'data_ingestion' && 'Fast-Track Data Upload — Upload historical data to accelerate configuration'}
              {currentView === 'ai_processing' && 'Customization Engine — LINK is learning your practice patterns'}
              {currentView === 'surgeon_welcome' && 'Surgeon\'s Golden Minute — Ready for first case'}
            </p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="flex items-center gap-2">
          {['Tier', 'Upload', 'Processing', 'Ready'].map((label, i) => {
            const stepIndex = i;
            const viewOrder: HubView[] = ['tier_select', 'data_ingestion', 'ai_processing', 'surgeon_welcome'];
            const currentIndex = viewOrder.indexOf(currentView);
            const isComplete = stepIndex < currentIndex;
            const isActive = stepIndex === currentIndex;
            return (
              <div key={label} className="flex items-center gap-2 flex-1">
                <div className="flex items-center gap-2 flex-1">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
                    isComplete ? 'bg-primary border-primary text-primary-foreground' :
                    isActive ? 'border-primary text-primary' :
                    'border-border text-muted-foreground'
                  }`}>
                    {isComplete ? '✓' : i + 1}
                  </div>
                  <span className={`text-xs font-medium hidden sm:inline ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
                    {label}
                  </span>
                </div>
                {i < 3 && <div className={`h-px flex-1 ${isComplete ? 'bg-primary' : 'bg-border'}`} />}
              </div>
            );
          })}
        </div>

        {/* Views */}
        {currentView === 'tier_select' && (
          <OnboardingTierSelector onSelect={handleTierSelect} />
        )}
        {currentView === 'data_ingestion' && selectedTier && (
          <DataIngestionPortal tier={selectedTier} onComplete={handleDataComplete} />
        )}
        {currentView === 'ai_processing' && (
          <AICustomizationEngine onComplete={handleProcessingComplete} />
        )}
        {currentView === 'surgeon_welcome' && (
          <SurgeonWelcomeScreen onStartCase={() => navigate('/surgeon')} />
        )}
      </div>
    </OpsLayout>
  );
}
